/**
 * Генерирует криптографически безопасное случайное целое число в заданном диапазоне [min, max].
 * Использует Web Crypto API для получения криптостойких случайных значений.
 *
 * @param {number} min - Минимальное значение диапазона (включительно)
 * @param {number} max - Максимальное значение диапазона (включительно)
 * @returns {number} Случайное число в диапазоне [min, max]
 * @throws {RangeError} Если min > max
 * @example
 * const randomNum = secureRandomInt(1, 10); // Возвращает число от 1 до 10 включительно
 * const otpCode = secureRandomInt(100000, 999999); // Использование для генерации OTP-кода (6 цифр)
 */
export const secureRandomInt = (min: number, max: number): number => {
    if (min > max) {
        throw new RangeError('Минимальное значение не может быть больше, чем максимальное');
    }

    const range = max - min + 1;
    const array = new Uint32Array(1);
    let value;
    const maxSafe = Math.floor(0xffffffff / range) * range;

    do {
        crypto.getRandomValues(array);
        // eslint-disable-next-line prefer-destructuring
        value = array[0];
    } while (value >= maxSafe);

    return min + (value % range);
};

/**
 * Генерирует криптографически безопасную случайную строку заданной длины.
 * Использует Web Crypto API для получения случайных значений и преобразует их в строку
 * с помощью base-36 кодирования (цифры 0-9 и буквы a-z).
 *
 * @param {number} [length=16] - Длина генерируемой строки (по умолчанию 16 символов)
 * @returns {string} Случайная строка из цифр и букв a-z
 * @throws {Error} Если length не является положительным целым числом
 * @example
 * const randomString = generateRandomString(); // Генерация стандартной строки (16 символов)
 * const token = generateRandomString(32); // Генерация токена (32 символа)
 * const shortCode = generateRandomString(8); // Генерация короткого кода (8 символов)
 */
export const generateRandomString = (length = 16): string => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(36).padStart(2, '0')).join('');
};
